import { useEffect, useRef } from 'react'

type Props = {
  stream: MediaStream | null
  /** While false the rAF loop is paused to save battery. */
  active: boolean
  className?: string
  /** Number of frequency bins drawn — lower = chunkier bars, higher = finer. */
  bars?: number
  /** DPR-aware canvas sizing. */
  height?: number
}

/**
 * Draws live audio frequency bars from a MediaStream. The analyser lives for
 * the lifetime of the stream; the rAF loop only runs while `active` so pausing
 * the recorder drops the visualizer to the last frame (no battery drain).
 */
export default function Waveform({ stream, active, className, bars = 48, height = 96 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const ctxRef = useRef<AudioContext | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const rafRef = useRef<number | null>(null)
  const dataRef = useRef<Uint8Array | null>(null)

  // (Re-)wire the analyser whenever the stream identity changes.
  useEffect(() => {
    cleanupAudio()
    if (!stream) return
    try {
      const AC =
        (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
          .AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!AC) return
      const ctx = new AC()
      const source = ctx.createMediaStreamSource(stream)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 128
      analyser.smoothingTimeConstant = 0.65
      source.connect(analyser)
      ctxRef.current = ctx
      sourceRef.current = source
      analyserRef.current = analyser
      // Explicit ArrayBuffer backing — newer TS lib.dom types reject
      // SharedArrayBuffer-backed views in getByteFrequencyData.
      dataRef.current = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount))
    } catch {
      // Older browsers without Web Audio — fall through, waveform stays blank.
    }
    return () => {
      cleanupAudio()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream])

  // Toggle the rAF loop based on `active`.
  useEffect(() => {
    if (!active) {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      return
    }
    if (!analyserRef.current || !dataRef.current || !canvasRef.current) return

    const canvas = canvasRef.current
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const resize = () => {
      const w = canvas.clientWidth
      const h = canvas.clientHeight
      canvas.width = Math.max(1, Math.floor(w * dpr))
      canvas.height = Math.max(1, Math.floor(h * dpr))
    }
    resize()
    const ro = new ResizeObserver(resize)
    ro.observe(canvas)

    const render = () => {
      rafRef.current = requestAnimationFrame(render)
      const analyser = analyserRef.current
      const data = dataRef.current
      const ctx2d = canvas.getContext('2d')
      if (!analyser || !data || !ctx2d) return
      // TS 5.5+ typed-array generics require ArrayBuffer-backed views here;
      // our `data` is exactly that, but the inferred type is ArrayBufferLike.
      analyser.getByteFrequencyData(data as unknown as Uint8Array<ArrayBuffer>)
      const w = canvas.width
      const h = canvas.height
      ctx2d.clearRect(0, 0, w, h)
      const step = Math.max(1, Math.floor(data.length / bars))
      const gap = Math.max(1, Math.round(2 * dpr))
      const barW = Math.max(1, Math.floor((w - gap * (bars - 1)) / bars))
      ctx2d.fillStyle = '#2a56b0' // brand-600
      for (let i = 0; i < bars; i++) {
        const v = data[i * step] / 255
        const bh = Math.max(2 * dpr, v * h * 0.95)
        const x = i * (barW + gap)
        const y = (h - bh) / 2
        ctx2d.fillRect(x, y, barW, bh)
      }
    }
    rafRef.current = requestAnimationFrame(render)
    return () => {
      ro.disconnect()
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [active, bars])

  function cleanupAudio() {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    try {
      sourceRef.current?.disconnect()
    } catch {
      // ignore
    }
    try {
      analyserRef.current?.disconnect()
    } catch {
      // ignore
    }
    sourceRef.current = null
    analyserRef.current = null
    dataRef.current = null
    const ctx = ctxRef.current
    ctxRef.current = null
    if (ctx && ctx.state !== 'closed') void ctx.close()
  }

  return (
    <canvas
      ref={canvasRef}
      className={className ?? 'w-full rounded-md bg-slate-100'}
      style={{ height }}
      aria-hidden
    />
  )
}
